import { useCallback, useEffect, useMemo, useReducer } from "react";
import { useQuery } from "@tanstack/react-query";
import debounce from "lodash.debounce";
import { getCourses, getCategories } from "../../../api/courses";
import { useParams, useSearchParams } from "react-router-dom";

// --- Tipe untuk parameter hook ---
interface UseContentListParams {
  initialPage?: number;
  initialLimit?: number;
  initialCategories?: string[];
  initialLevels?: string[];
  initialSearchTerm?: string;
  initialSortOrder?: string;
  type?: string;
  enableUrlParams?: boolean; // Tambahan parameter untuk mengaktifkan URL params
}

// --- Definisi State Filter dan Action untuk Reducer ---
interface CourseFiltersState {
  selectedCategories: string[];
  selectedLevels: string[];
  searchTerm: string;
  sortOrder: string;
  currentPage: number;
  limit: number;
}

type FilterAction =
  | { type: "TOGGLE_CATEGORY"; payload: { value: string; checked: boolean } }
  | { type: "TOGGLE_LEVEL"; payload: { value: string; checked: boolean } }
  | { type: "SET_SEARCH_TERM"; payload: string }
  | { type: "SET_SORT_ORDER"; payload: string }
  | { type: "SET_PAGE"; payload: number }
  | { type: "SET_CATEGORIES"; payload: string[] }
  | { type: "SET_LEVELS"; payload: string[] }
  | { type: "CLEAR_ALL_FILTERS" }
  | { type: "SET_LIMIT"; payload: number };

// --- Fungsi Reducer ---
function filtersReducer(
  state: CourseFiltersState,
  action: FilterAction,
): CourseFiltersState {
  switch (action.type) {
    case "TOGGLE_CATEGORY": {
      const newCategories = action.payload.checked
        ? [...state.selectedCategories, action.payload.value]
        : state.selectedCategories.filter(
            (cat) => cat !== action.payload.value,
          );
      return { ...state, selectedCategories: newCategories, currentPage: 1 }; // Reset halaman
    }
    case "TOGGLE_LEVEL": {
      const newLevels = action.payload.checked
        ? [...state.selectedLevels, action.payload.value]
        : state.selectedLevels.filter(
            (level) => level !== action.payload.value,
          );
      return { ...state, selectedLevels: newLevels, currentPage: 1 }; // Reset halaman
    }
    case "SET_CATEGORIES":
      return { ...state, selectedCategories: action.payload, currentPage: 1 };
    case "SET_LEVELS":
      return { ...state, selectedLevels: action.payload, currentPage: 1 };
    case "SET_SEARCH_TERM":
      return { ...state, searchTerm: action.payload, currentPage: 1 }; // Reset halaman
    case "SET_SORT_ORDER":
      return { ...state, sortOrder: action.payload, currentPage: 1 }; // Reset halaman
    case "SET_PAGE":
      return { ...state, currentPage: action.payload };
    case "CLEAR_ALL_FILTERS":
      return {
        ...state,
        selectedCategories: [],
        selectedLevels: [],
        searchTerm: "",
        sortOrder: "",
        currentPage: 1,
      };
    case "SET_LIMIT":
      return { ...state, limit: action.payload };
    default:
      return state;
  }
}

// --- Fungsi helper untuk konversi slug ke ID ---
const convertSlugToId = (
  slug: string,
  items: any[],
  field: string = "name",
): string | null => {
  const item = items?.find(
    (item) =>
      item[field]?.toLowerCase().replace(/\s+/g, "-") === slug.toLowerCase(),
  );
  return item?.id?.toString() || null;
};

const convertIdToSlug = (
  id: string,
  items: any[],
  field: string = "name",
): string | null => {
  const item = items?.find((item) => item.id?.toString() === id);
  return item ? item[field]?.toLowerCase().replace(/\s+/g, "-") : null;
};

export const useContentList = ({
  initialPage = 1,
  initialLimit = 12,
  initialCategories = [],
  initialLevels = [],
  initialSearchTerm = "",
  initialSortOrder = "",
  type,
  enableUrlParams = false,
}: UseContentListParams = {}) => {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // --- Inisialisasi State dengan useReducer ---
  const [filters, dispatch] = useReducer(filtersReducer, {
    selectedCategories: initialCategories,
    selectedLevels: initialLevels,
    searchTerm: initialSearchTerm,
    sortOrder: initialSortOrder,
    currentPage: initialPage,
    limit: initialLimit,
  });

  // --- Fetch categories untuk konversi slug ---
  const { data: categoriesData } = useQuery({
    queryKey: ["categories", { page: 1, limit: 100 }],
    queryFn: () => getCategories({ page: 1, limit: 100 }, false),
    enabled: enableUrlParams,
  });

  // --- Effect untuk membaca URL parameters ---
  useEffect(() => {
    if (!enableUrlParams) return;

    const urlCategory = params.category;
    const urlLevel = params.level;
    const searchTerm = searchParams.get("search") || "";
    const sortOrder = searchParams.get("sort") || "";

    let newCategories = [...initialCategories];
    let newLevels = [...initialLevels];

    // Konversi category slug ke ID
    if (urlCategory && categoriesData?.data) {
      const categoryId = convertSlugToId(urlCategory, categoriesData.data);
      if (categoryId) {
        newCategories = [categoryId];
      }
    }

    // Konversi level slug ke ID
    if (urlLevel) {
      const levelMap: { [key: string]: string } = {
        beginner: "BEGINNER",
        intermediate: "INTERMEDIATE",
        advance: "ADVANCE",
        expert: "EXPERT",
      };
      const levelId = levelMap[urlLevel.toLowerCase()];
      if (levelId) {
        newLevels = [levelId];
      }
    }

    // Update state jika ada perubahan
    if (
      JSON.stringify(newCategories) !==
      JSON.stringify(filters.selectedCategories)
    ) {
      dispatch({ type: "SET_CATEGORIES", payload: newCategories });
    }
    if (JSON.stringify(newLevels) !== JSON.stringify(filters.selectedLevels)) {
      dispatch({ type: "SET_LEVELS", payload: newLevels });
    }
    if (searchTerm !== filters.searchTerm) {
      dispatch({ type: "SET_SEARCH_TERM", payload: searchTerm });
    }
    if (sortOrder !== filters.sortOrder) {
      dispatch({ type: "SET_SORT_ORDER", payload: sortOrder });
    }
  }, [
    params.category,
    params.level,
    searchParams,
    categoriesData?.data,
    enableUrlParams,
    initialCategories,
    initialLevels,
    filters.searchTerm,
    filters.sortOrder,
    filters.selectedCategories,
    filters.selectedLevels,
  ]);

  const fetchCourses = useCallback(async () => {
    const apiParams: any = {
      page: filters.currentPage,
      limit: filters.limit,
      type,
    };

    if (filters.selectedCategories.length > 0) {
      apiParams.category = filters.selectedCategories.join(",");
    }
    if (filters.selectedLevels.length > 0) {
      apiParams.level = filters.selectedLevels.join(",");
    }
    if (filters.searchTerm) {
      apiParams.search = filters.searchTerm;
    }
    if (filters.sortOrder === "free") {
      apiParams.is_free = "true";
    }

    return getCourses(apiParams, false);
  }, [filters, type]);

  const { data, isLoading, isError, error, refetch, isRefetching } = useQuery<
    any,
    Error
  >({
    queryKey: ["courses", filters],
    queryFn: fetchCourses,
  });

  useEffect(() => {
    if (type) refetch();
  }, [type, refetch]);

  // --- Handlers Perubahan Filter (sekarang lebih sederhana) ---
  const handleCategoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: "TOGGLE_CATEGORY",
      payload: { value: e.target.value, checked: e.target.checked },
    });
  };

  const handleLevelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: "TOGGLE_LEVEL",
      payload: { value: e.target.value, checked: e.target.checked },
    });
  };

  // --- Debounce untuk Pencarian (distabilkan dengan useMemo agar tidak recreate di setiap render) ---
  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        dispatch({ type: "SET_SEARCH_TERM", payload: value });
        if (enableUrlParams) {
          setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            if (value) params.set("search", value);
            else params.delete("search");
            return params;
          });
        }
      }, 500),
    [enableUrlParams, setSearchParams],
  );

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSetSearchTerm(e.target.value);
  };

  // Bersihkan debounce saat hook unmount
  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.cancel();
    };
  }, [debouncedSetSearchTerm]);

  // --- Handler untuk Sorting ---
  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch({ type: "SET_SORT_ORDER", payload: e.target.value });
    if (enableUrlParams) {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (e.target.value) params.set("sort", e.target.value);
        else params.delete("sort");
        return params;
      });
    }
  };

  // --- Handler untuk Pagination (onPaginationChange) ---
  const onPaginationChange = (current: number, pageSize: number) => {
    dispatch({ type: "SET_PAGE", payload: current });
    dispatch({ type: "SET_LIMIT", payload: pageSize });
  };

  // --- Clear Filters ---
  const clearAllFilters = () => {
    dispatch({ type: "CLEAR_ALL_FILTERS" });
  };

  const lastPage = data?.last_page || 1;
  const currentPageData = data?.current_page || 1;
  const total = data?.total || 0;

  // --- Helper functions untuk konversi ---
  const getCategorySlug = (id: string) => {
    return convertIdToSlug(id, categoriesData?.data || []);
  };

  const getLevelSlug = (level: string) => {
    const levelMap: { [key: string]: string } = {
      BEGINNER: "beginner",
      INTERMEDIATE: "intermediate",
      ADVANCE: "advance",
      EXPERT: "expert",
    };
    return levelMap[level] || level.toLowerCase();
  };

  // --- Nilai yang dikembalikan hook ---
  return {
    // Data Query
    content: data?.data || [],
    isLoading,
    isRefetching,
    isError,
    error,
    refetch,

    lastPage,
    currentPageData,
    total,

    // Filter State (diambil dari objek 'filters')
    selectedCategories: filters.selectedCategories,
    selectedLevels: filters.selectedLevels,
    searchTerm: filters.searchTerm,
    sortOrder: filters.sortOrder,
    currentPage: filters.currentPage,
    limit: filters.limit,

    // Filter Handlers (sekarang lebih sederhana karena hanya memanggil dispatch)
    handleCategoryChange,
    handleLevelChange,
    handleSearchInputChange,
    handleSortChange,
    onPaginationChange,
    clearAllFilters,

    // Helper functions untuk konversi
    getCategorySlug,
    getLevelSlug,
    convertSlugToId,
    convertIdToSlug,
  };
};
